/**
 * `/ask` — the front door.
 *
 * One box, one question, an answer that streams back with its sources, the
 * actions it wants permission to take, and the tickets it touched. Everything
 * else on this page is in service of that.
 *
 * STATE MODEL
 * -----------
 * The server owns the transcript; this component owns only the *turn in
 * flight*. `postAssistantMessage` returns the user's message, a reserved
 * assistant message and a `stream_path`; those three live in `live` until the
 * stream finishes and the refetched conversation contains the same message id,
 * at which point the local copy is dropped and the server's version takes over.
 * That handover is why a mid-answer refresh loses nothing: the conversation
 * query alone can rebuild the page.
 *
 * The conversation id lives in the query string (`?c=…`) rather than in state,
 * so a reload, a back button, or a shared link all land in the right thread.
 *
 * CHAT FIRST, NEVER STUCK
 * -----------------------
 * The page is one chat input and nothing to click beside it: the assistant
 * classifies what the person wrote and guides them from there (an incident
 * card, a catalog form, their tickets). The plain forms appear only as simple
 * inline links when the assistant cannot take the conversation —
 * `/meta/capabilities` says it is not running, or a turn failed with
 * `ASSISTANT_UNAVAILABLE` (`showsFallbackLinks`).
 */

import { Icon, Plus } from "@norican/icons";
import { Alert, Button, Card, cn, Typography } from "@norican/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import {
  ErrorState,
  LoadingState,
  Markdown,
  normaliseApiError,
  PageContainer,
} from "@/components/common";
import { MySupportRail } from "@/features/support";
import { useMyItActivity } from "@/features/tickets";
import { useDocumentTitle } from "@/hooks";
import { useLanguage, useT } from "@/i18n";
import { useKiosk } from "@/lib/api";
import { logger } from "@/lib/logger";

import { AskHero } from "./ask-hero";
import { AssistantAnswer } from "./assistant-answer";
import { useAssistantCapability } from "./assistant-capability";
import { showsFallbackLinks } from "./assistant-fallback";
import { AssistantFallbackLinks } from "./assistant-fallback-links";
import { Composer, type ComposerHandle } from "./composer";
import type {
  AssistantAccessCardEvent,
  AssistantIncidentFormEvent,
  AssistantMessage,
  AssistantRequestFormEvent,
} from "./contract";
import { DemoCorpusNotice } from "./demo-corpus";
import {
  useCancelAssistantMessage,
  useConversation,
  useCreateConversation,
  usePostAssistantMessage,
} from "./hooks";
import { InlineFormsProvider, useOpenInlineForms } from "./inline-forms";
import { type ChatFile, chatFilesUpTo } from "./lib/chat-files";
import { composerMode } from "./lib/inline-forms";
import { MessageBubble } from "./message-bubble";
import { MessageCentre } from "./message-centre";
import { useAssistantStream } from "./use-assistant-stream";

/** Query-string key holding the open conversation. */
const CONVERSATION_PARAM = "c";

/** Breathing room above the turn so it does not sit flush under the header. */
const SCROLL_HEADROOM_PX = 96;

/** How many times the transcript is refetched waiting for a finished turn to persist. */
const MAX_SETTLE_ATTEMPTS = 3;
/** Pause between those attempts. */
const SETTLE_DELAY_MS = 800;

interface LiveTurn {
  userMessage: AssistantMessage;
  assistantMessage: AssistantMessage;
  streamPath: string;
  /** Kept so "Ask again" can resend the same question. */
  prompt: string;
}

export function AskPage() {
  // 14 Oct: the chat forms report whether they are open, for the bar below.
  return (
    <InlineFormsProvider>
      <AskPageBody />
    </InlineFormsProvider>
  );
}

function AskPageBody() {
  const { t } = useT("assistant");
  const { t: tt } = useT("tickets");
  /* Drives the grid: with nothing in flight there is no rail, and the
     conversation gets the full width instead of an empty 18rem column. */
  const activity = useMyItActivity();
  /* No rail on a shared device: it lists the device's tickets and chats,
     which are nobody's in particular (1 Oct). */
  const kiosk = useKiosk();
  const showRail = !activity.isEmpty && kiosk === null;
  const { locale } = useLanguage();
  const capability = useAssistantCapability();
  const assistantUnavailable = capability.available === false;
  useDocumentTitle(t("title"));

  const [searchParams, setSearchParams] = useSearchParams();
  const conversationId = searchParams.get(CONVERSATION_PARAM);

  const [live, setLive] = useState<LiveTurn | null>(null);
  const [draft, setDraft] = useState("");
  const composerRef = useRef<ComposerHandle>(null);
  const location = useLocation();
  const navigate = useNavigate();
  /**
   * Text another page asked to put in the box (6 Oct): the account menu's
   * "Feedback" opens the chat with "I'd like to give feedback…" ready to
   * finish. Taken once, then the history entry's state is cleared, so a reload
   * or Back does not put it there again.
   */
  const prefill = (location.state as { prefill?: unknown } | null)?.prefill;
  useEffect(() => {
    if (typeof prefill !== "string" || prefill === "") {
      return;
    }
    setDraft(prefill);
    composerRef.current?.focus();
    void navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null },
    );
  }, [prefill, navigate, location.pathname, location.search]);
  /**
   * A resend from a failed reply's "Try again" whose turn could not start.
   *
   * That prompt is not in the composer (its first send was accepted and
   * cleared the box), so the start-error "Try again" resends it from here;
   * otherwise it sends the draft the composer kept.
   */
  const unstartedRetryPrompt = useRef<string | null>(null);

  /**
   * Catalog forms the assistant streamed, kept against the message that carried
   * them.
   *
   * They are stream-only: the persisted `AssistantMessage` has no field to put
   * them in, so once the finished turn hands over to the server's copy the
   * event is gone. Holding them here is what stops a half-filled form
   * disappearing under the user the moment the answer finishes streaming. A
   * reload still loses it — the assistant offers the form again — which is why
   * this is page state and not something dressed up as a cache.
   */
  const [formsByMessage, setFormsByMessage] = useState<Record<string, AssistantRequestFormEvent[]>>(
    {},
  );
  /** The same, for the "Start an access request" cards (6 Oct). */
  const [accessCardsByMessage, setAccessCardsByMessage] = useState<
    Record<string, AssistantAccessCardEvent[]>
  >({});
  /** The same, for the prefilled incident reports the assistant streams. */
  const [incidentFormsByMessage, setIncidentFormsByMessage] = useState<
    Record<string, AssistantIncidentFormEvent[]>
  >({});
  /**
   * Draft attachment ids each turn carried, against the assistant message that
   * answered it.
   *
   * The persisted `AssistantMessage` does not carry the user's attachments
   * either, and an incident raised from the answer wants them: a screenshot of
   * the error is the most useful thing on the ticket. Recorded when the turn is
   * sent, which is the only moment the browser knows them.
   */
  const [attachmentsByMessage, setAttachmentsByMessage] = useState<Record<string, string[]>>({});
  /**
   * The same files with their names, for the request form cards.
   *
   * A request card carries every file attached in the chat up to its turn
   * (`chatFilesUpTo`) and shows them by name, so the person can see — and
   * take off — what goes to IT with the request (LIVE #10578, 8 Oct).
   */
  const [chatFilesByMessage, setChatFilesByMessage] = useState<Record<string, ChatFile[]>>({});
  const turnAnchor = useRef<HTMLDivElement>(null);
  /**
   * A turn is in flight, from the click until the live turn is on screen.
   *
   * Deriving this from the two mutations' `isPending` leaves a gap: between
   * `createConversation` resolving and `postMessage` being called both are
   * idle, and if the conversation query resolves inside that window every
   * "nothing to show" test is true at once and the page flashes its full
   * landing hero mid-send. One flag, set before the first await and cleared
   * only once `live` is set, has no such window.
   */
  const [turnInFlight, setTurnInFlight] = useState(false);
  const settleAttempts = useRef<{ messageId: string | null; count: number }>({
    messageId: null,
    count: 0,
  });

  const conversation = useConversation(conversationId);
  const createConversation = useCreateConversation();
  const postMessage = usePostAssistantMessage();
  const cancelMessage = useCancelAssistantMessage();
  const stream = useAssistantStream(live?.streamPath ?? null);

  const persistedMessages = conversation.data?.messages ?? [];
  const streamedMessageId = stream.message?.message_id ?? null;

  const streamPhase = stream.phase;
  const refetchConversation = conversation.refetch;
  const conversationIsFetching = conversation.isFetching;
  // Persisted *and finished*. The conversation is fetched while the turn is
  // being accepted, so the server copy usually exists already — as PENDING.
  // Handing over to that copy on `done` swapped a finished (often failed)
  // reply for a stale "Thinking…" that nothing ever refreshed.
  const streamedMessagePersisted =
    streamedMessageId !== null &&
    persistedMessages.some(
      (message) =>
        message.message_id === streamedMessageId &&
        message.status !== "PENDING" &&
        message.status !== "STREAMING",
    );

  /**
   * Hand the finished turn back to the server copy.
   *
   * This is not data loading — it reacts to the stream reaching `done` by
   * refetching the thread, then drops the local turn as soon as the refetch
   * contains it. Dropping it any earlier would blank the answer for a frame,
   * and never dropping it would show the reply twice.
   *
   * The attempt counter is the important bit: if the write has not landed
   * upstream yet, an unbounded "refetch until it appears" turns into a polling
   * loop against Ivanti. Three tries, then the local copy simply stays.
   */
  useEffect(() => {
    if (streamPhase !== "done" || streamedMessageId === null) {
      return undefined;
    }
    if (streamedMessagePersisted) {
      setLive(null);
      return undefined;
    }
    if (settleAttempts.current.messageId !== streamedMessageId) {
      settleAttempts.current = { messageId: streamedMessageId, count: 0 };
    }
    if (settleAttempts.current.count >= MAX_SETTLE_ATTEMPTS || conversationIsFetching) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      settleAttempts.current.count += 1;
      void refetchConversation();
    }, SETTLE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [
    conversationIsFetching,
    refetchConversation,
    streamPhase,
    streamedMessagePersisted,
    streamedMessageId,
  ]);

  const liveAssistantMessageId = live?.assistantMessage.message_id ?? null;
  const streamedForms = stream.requestForms;
  const streamedIncidentForms = stream.incidentForms;
  const streamedAccessCards = stream.accessCards;

  useEffect(() => {
    if (liveAssistantMessageId === null || streamedAccessCards.length === 0) {
      return;
    }
    setAccessCardsByMessage((previous) =>
      previous[liveAssistantMessageId] === streamedAccessCards
        ? previous
        : { ...previous, [liveAssistantMessageId]: streamedAccessCards },
    );
  }, [liveAssistantMessageId, streamedAccessCards]);

  useEffect(() => {
    if (liveAssistantMessageId === null || streamedForms.length === 0) {
      return;
    }
    setFormsByMessage((previous) =>
      previous[liveAssistantMessageId] === streamedForms
        ? previous
        : { ...previous, [liveAssistantMessageId]: streamedForms },
    );
  }, [liveAssistantMessageId, streamedForms]);

  useEffect(() => {
    if (liveAssistantMessageId === null || streamedIncidentForms.length === 0) {
      return;
    }
    setIncidentFormsByMessage((previous) =>
      previous[liveAssistantMessageId] === streamedIncidentForms
        ? previous
        : { ...previous, [liveAssistantMessageId]: streamedIncidentForms },
    );
  }, [liveAssistantMessageId, streamedIncidentForms]);

  const openConversation = useCallback(
    (nextId: string | null) => {
      setLive(null);
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (nextId === null) {
            next.delete(CONVERSATION_PARAM);
          } else {
            next.set(CONVERSATION_PARAM, nextId);
          }
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  /** Resolves `true` once the turn is accepted, `false` when it could not start. */
  const send = useCallback(
    async (
      content: string,
      attachments: string[] = [],
      files: ChatFile[] = [],
    ): Promise<boolean> => {
      setTurnInFlight(true);
      try {
        let targetId = conversationId;
        if (targetId === null) {
          const created = await createConversation.mutateAsync({ locale });
          targetId = created.conversation_id;
          setSearchParams(
            (previous) => {
              const next = new URLSearchParams(previous);
              next.set(CONVERSATION_PARAM, created.conversation_id);
              return next;
            },
            { replace: true },
          );
        }

        const accepted = await postMessage.mutateAsync({
          conversationId: targetId,
          // `content_lang` records the language the user actually typed in. The
          // backend stores it verbatim under that tag and never translates a write.
          //
          // `attachments` carries the draft ids of the files the user attached
          // to *this* message. The server injects them into whatever
          // create_ticket runs during the turn — the model never sees or
          // chooses an attachment id (§ safety property).
          body: { content, content_lang: locale, attachments },
        });

        logger.debug("[assistant] turn accepted", accepted.assistant_message.message_id);
        if (attachments.length > 0) {
          setAttachmentsByMessage((previous) => ({
            ...previous,
            [accepted.assistant_message.message_id]: attachments,
          }));
          setChatFilesByMessage((previous) => ({
            ...previous,
            // Every id the turn carried, named where the composer knew the name.
            [accepted.assistant_message.message_id]: attachments.map(
              (attachmentId) =>
                files.find((file) => file.attachmentId === attachmentId) ?? {
                  attachmentId,
                  name: attachmentId,
                },
            ),
          }));
        }
        setLive({
          userMessage: accepted.user_message,
          assistantMessage: accepted.assistant_message,
          streamPath: accepted.stream_path,
          prompt: content,
        });
        // The draft is not cleared here: the composer empties itself once this
        // resolves `true`, and keeps text and attachments when it resolves
        // `false`, so a failed send never throws away what was written.
        setTurnInFlight(false);
        return true;
      } catch (error) {
        // Surfaced by the inline Alert below — `mutateAsync` would otherwise
        // reject unhandled and land in the console as a noisy promise error.
        setTurnInFlight(false);
        logger.warn("[assistant] could not start the turn", error);
        return false;
      }
    },
    [conversationId, createConversation, locale, postMessage, setSearchParams],
  );

  /**
   * "New question" resets the thread. When the conversation is already empty
   * every part of that reset is a no-op, so it also clears any draft and puts
   * the caret back in the box — otherwise the button looks broken in exactly
   * the state a new user meets it in.
   */
  const startNewQuestion = useCallback(() => {
    openConversation(null);
    setDraft("");
    composerRef.current?.focus();
  }, [openConversation]);

  // 14 Oct: while a form in the chat is open and unsent, a slim bar takes
  // the composer's place ("fill in the form above"), unless the person asks
  // to type instead; sent or cancelled, the composer is back.
  const openForms = useOpenInlineForms();
  const [askedInsteadFor, setAskedInsteadFor] = useState<readonly string[]>([]);
  const mode = composerMode(openForms, askedInsteadFor);

  const handleStop = () => {
    if (live === null) {
      return;
    }
    cancelMessage.mutate({
      conversationId: live.assistantMessage.conversation_id,
      messageId: live.assistantMessage.message_id,
    });
  };

  const handleRetry = () => {
    if (live === null) {
      return;
    }
    const { prompt, assistantMessage } = live;
    // No `done` yet means the server may still hold the reply as in flight
    // (e.g. a restart orphaned it, which the stream reports as a recoverable
    // error). Cancel it first, or the resend is refused as CONVERSATION_BUSY.
    // Cancel is idempotent, so a reply that did finish meanwhile is untouched.
    const unfinished = stream.message === null;
    setLive(null);
    stream.reset();
    void (async () => {
      if (unfinished) {
        try {
          await cancelMessage.mutateAsync({
            conversationId: assistantMessage.conversation_id,
            messageId: assistantMessage.message_id,
          });
        } catch (error) {
          logger.warn("[assistant] could not cancel the unfinished reply before retrying", error);
        }
      }
      const started = await send(prompt);
      unstartedRetryPrompt.current = started ? null : prompt;
    })();
  };

  /**
   * Bring the new turn to the top of the viewport once it exists.
   *
   * This used to run inside a `requestAnimationFrame` in `send()`, immediately
   * after `setLive(...)`. That raced React's commit: the anchor is only
   * rendered when `live` is set, so the ref was usually still null when the
   * frame fired and nothing scrolled at all. Meanwhile the hero collapses and
   * the transcript grows, so the content under a stationary viewport shifts —
   * which reads as the page lurching somewhere else rather than as a send.
   *
   * Keyed on the message id, not the object, so it fires once per turn instead
   * of on every stream update.
   */
  const liveTurnId = live?.assistantMessage.message_id ?? null;
  useEffect(() => {
    if (liveTurnId === null) {
      return;
    }
    const anchor = turnAnchor.current;
    // An anchor with no height is the tail of the document, and asking the
    // browser to put that at the top of the viewport means "scroll as far as
    // you can" — which lands on the composer with the question and the answer
    // both above the fold. That happens whenever the user's message has
    // already moved into the persisted transcript, leaving this block empty
    // until the first token arrives.
    if (anchor === null || anchor.getBoundingClientRect().height === 0) {
      return;
    }
    // Deliberately instant, not smooth. A smooth scroll is animated over
    // several hundred milliseconds, and the answer is streaming into the
    // document the whole time — the target keeps moving underneath the
    // animation and it overshoots. Position is computed once, from where the
    // turn is now, and nothing that arrives later can drag the viewport.
    const top = anchor.getBoundingClientRect().top + window.scrollY - SCROLL_HEADROOM_PX;
    window.scrollTo({ top: Math.max(top, 0), behavior: "auto" });
  }, [liveTurnId]);

  const startError = createConversation.error ?? postMessage.error;
  const isStarting = turnInFlight || createConversation.isPending || postMessage.isPending;
  /**
   * Return the caret to the message box the moment a turn finishes.
   *
   * The point is that this reads as one conversation rather than a sequence of
   * forms: reply, read, and carry on typing without reaching for the mouse.
   *
   * `preventScroll` is the whole reason this is safe. Focusing an element the
   * browser considers off-screen scrolls it into view by default, which would
   * drag anyone who had scrolled up to re-read an answer straight back to the
   * bottom — exactly the behaviour a transcript must not have.
   */
  const busy = stream.isStreaming || isStarting;

  const wasBusy = useRef(false);
  useEffect(() => {
    if (wasBusy.current && !busy) {
      composerRef.current?.focus({ preventScroll: true });
    }
    wasBusy.current = busy;
  }, [busy]);

  /** The conversation's assistant turns in order, the live one last. */
  const persistedAssistantIds = persistedMessages
    .filter((message) => message.role === "ASSISTANT")
    .map((message) => message.message_id);
  const assistantMessageIds =
    live === null || persistedAssistantIds.includes(live.assistantMessage.message_id)
      ? persistedAssistantIds
      : [...persistedAssistantIds, live.assistantMessage.message_id];

  const liveUserAlreadyPersisted =
    live !== null &&
    persistedMessages.some((message) => message.message_id === live.userMessage.message_id);

  // "Empty" means there is genuinely nothing to show — not that the thread is
  // still loading or failed to load, both of which have their own state above,
  // and not that a turn is on its way.
  //
  // Starting the first turn passes through a moment where all three of those
  // are briefly false: the conversation has been created and fetched (so it is
  // resolved), it is genuinely empty (the message has not been persisted yet),
  // and `live` is not set until `postMessage` resolves. Without `isStarting`
  // the page falls all the way back to the full hero and the suggestion list
  // in that gap — it reads as being thrown back to the landing page mid-send,
  // with the send button still spinning, before the answer finally appears.
  const conversationUnresolved =
    conversationId !== null && (conversation.isPending || conversation.isError);
  const transcriptIsEmpty =
    !conversationUnresolved && live === null && !isStarting && persistedMessages.length === 0;

  return (
    <PageContainer width="wide">
      <div
        className={cn(
          "grid w-full gap-x-8 gap-y-6",
          showRail ? "lg:grid-cols-[minmax(0,1fr)_18rem]" : "lg:grid-cols-1",
        )}
      >
        {/* The reading column is capped well below the page width: a transcript
            that runs edge to edge on a desktop monitor is hard to read.

            Order matters and is deliberate. In the empty state there is no
            transcript, so the column collapses to hero → ask box → suggestions:
            one continuous invitation instead of a heading, a void and a form
            stranded at the bottom. The moment a transcript exists the same JSX
            reads hero → transcript → ask box, and the box pins itself to the
            foot of the viewport. Nothing is conditionally mounted to achieve
            that, so the composer keeps its identity — and the draft in it —
            across the handover. */}
        {/* The measure lives here, once, rather than on each child. Capping
            the composer, the notice, the prompt grid and the activity strip
            individually is what let them drift apart — the grid had no cap at
            all and ran to the column edge while the composer stopped at 48rem.
            One wrapper means they cannot disagree. */}
        <div className="flex w-full max-w-[var(--norican-size-container-lg)] min-w-0 flex-col gap-4">
          {/* My Support is a page now, reached from the header — there is no
              drawer here and no second copy of that control. What does belong
              here is the way out of the current thread: once a transcript
              exists the composer replies into it, so without this an employee
              with a finished conversation has nowhere to put an unrelated
              question. It appears only when there is something to leave. */}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <AskHero
              compact={!transcriptIsEmpty}
              intake={capability.mode === "intake"}
              className="min-w-0 flex-1"
            />
            {transcriptIsEmpty ? null : (
              <Button
                variant="outline"
                tone="neutral"
                size="sm"
                startIcon={<Icon icon={Plus} size="sm" />}
                onClick={startNewQuestion}
              >
                {t("conversation.new")}
              </Button>
            )}
          </div>

          {/* The real assistant is not running (Foundry unconfigured, or the
              scripted stand-in on purpose). Say so up front rather than let
              someone discover it from a canned reply, with the plain forms as
              simple links right in the notice. */}
          {assistantUnavailable ? (
            <Alert tone="warning" title={t("fallback.unavailableTitle")}>
              <div className="flex flex-col gap-2">
                <span>{t("fallback.unavailableBody")}</span>
                <AssistantFallbackLinks />
              </div>
            </Alert>
          ) : null}

          {/* Above the transcript and above the ask box, so it is read before
              an answer is — not discovered underneath one. Renders nothing at
              all when the real index is configured. */}
          <DemoCorpusNotice />

          {/* Only when opening a thread from history. During a send the
              conversation is fetched too, and swapping the column for a
              spinner makes the page flicker through a third state on the way
              to the answer. */}
          {conversationId !== null && conversation.isPending && !isStarting ? (
            <LoadingState label={t("conversation.loading")} />
          ) : conversationId !== null && conversation.isError ? (
            <ErrorState
              error={conversation.error}
              onRetry={() => void conversation.refetch()}
              actions={
                <Button variant="outline" tone="neutral" onClick={() => openConversation(null)}>
                  {t("conversation.new")}
                </Button>
              }
            />
          ) : null}

          {/* No empty state block here on purpose: the hero above already says
              what this page is for, and a second "How can we help?" card was
              exactly the thing leaving a hole in the middle of the page. */}
          {transcriptIsEmpty ? null : (
            <div className="flex flex-col gap-6">
              {/* The reply the live turn is streaming is rendered from the
                  stream below, never from its (stale, PENDING) server copy
                  as well. */}
              {persistedMessages
                .filter(
                  (message) =>
                    live === null || message.message_id !== live.assistantMessage.message_id,
                )
                .map((message) =>
                  message.role === "USER" ? (
                    <MessageBubble
                      key={message.message_id}
                      role="USER"
                      timestamp={message.created_at}
                    >
                      <Markdown>{message.content.text}</Markdown>
                    </MessageBubble>
                  ) : (
                    <MessageBubble
                      key={message.message_id}
                      role="ASSISTANT"
                      timestamp={message.completed_at ?? message.created_at}
                    >
                      <AssistantAnswer
                        content={message.content}
                        status={message.status}
                        citations={message.citations ?? []}
                        knowledgeConsulted={message.knowledge_consulted ?? false}
                        ticketRefs={message.ticket_refs ?? []}
                        requestForms={formsByMessage[message.message_id] ?? []}
                        incidentForms={incidentFormsByMessage[message.message_id] ?? []}
                        accessCards={accessCardsByMessage[message.message_id] ?? []}
                        requestFormScope={message.message_id}
                        attachmentIds={attachmentsByMessage[message.message_id] ?? []}
                        chatFiles={chatFilesUpTo(
                          assistantMessageIds,
                          chatFilesByMessage,
                          message.message_id,
                        )}
                        conversationId={message.conversation_id}
                        messageId={message.message_id}
                        myFeedback={message.my_feedback ?? null}
                        error={message.error}
                      />
                    </MessageBubble>
                  ),
                )}

              {live === null ? null : (
                <div ref={turnAnchor} className="flex flex-col gap-6">
                  {liveUserAlreadyPersisted ? null : (
                    <MessageBubble role="USER" timestamp={live.userMessage.created_at}>
                      <Markdown>{live.userMessage.content.text}</Markdown>
                    </MessageBubble>
                  )}

                  <MessageBubble
                    role="ASSISTANT"
                    timestamp={live.assistantMessage.created_at}
                    busy={stream.isStreaming}
                  >
                    <AssistantAnswer
                      streamingText={stream.text}
                      status={
                        stream.message?.status ??
                        (stream.phase === "error" ? "FAILED" : "STREAMING")
                      }
                      citations={stream.citations}
                      knowledgeConsulted={stream.message?.knowledge_consulted ?? false}
                      ticketRefs={stream.ticketRefs}
                      requestForms={stream.requestForms}
                      incidentForms={stream.incidentForms}
                      accessCards={stream.accessCards}
                      requestFormScope={live.assistantMessage.message_id}
                      attachmentIds={attachmentsByMessage[live.assistantMessage.message_id] ?? []}
                      chatFiles={chatFilesUpTo(
                        assistantMessageIds,
                        chatFilesByMessage,
                        live.assistantMessage.message_id,
                      )}
                      conversationId={live.assistantMessage.conversation_id}
                      messageId={live.assistantMessage.message_id}
                      activity={stream.activity}
                      error={stream.error ?? stream.message?.error ?? null}
                      transportFailed={stream.transportFailed}
                      announceCompletion
                      onRetry={handleRetry}
                    />
                  </MessageBubble>
                </div>
              )}
            </div>
          )}

          {startError === null ? null : (
            <ErrorState
              variant="inline"
              error={startError}
              // The draft is still in the box (the composer keeps it until a
              // send is accepted), so retrying is just sending it again.
              onRetry={
                isStarting
                  ? undefined
                  : () => {
                      const prompt = unstartedRetryPrompt.current;
                      if (prompt === null) {
                        composerRef.current?.submit();
                        return;
                      }
                      void send(prompt).then((started) => {
                        if (started) {
                          unstartedRetryPrompt.current = null;
                        }
                      });
                    }
              }
              actions={
                showsFallbackLinks({
                  available: capability.available,
                  errorCode: normaliseApiError(startError).code,
                }) ? (
                  <AssistantFallbackLinks />
                ) : undefined
              }
            />
          )}

          {cancelMessage.isError ? (
            <ErrorState variant="inline" error={cancelMessage.error} />
          ) : null}

          {/* The ask box is the point of the page, so it is given a raised
              surface of its own rather than a rule and some whitespace. While
              the page is empty it is the hero's landing pad and picks up an
              accent edge; once a transcript is running it pins to the bottom of
              the viewport, where an opaque card is also what stops the answer
              scrolling through it. */}
          <Card
            variant="elevated"
            padding={mode === "formBar" ? "sm" : "lg"}
            className={cn(
              transcriptIsEmpty
                ? "ring-accent-border shadow-md ring-1"
                : "sticky bottom-0 z-10 shadow-lg",
            )}
          >
            {mode === "formBar" ? (
              <FormOpenBar
                onAskInstead={() => {
                  setAskedInsteadFor(openForms);
                  window.setTimeout(() => composerRef.current?.focus(), 0);
                }}
                onNewQuestion={startNewQuestion}
              />
            ) : null}
            <div className={cn(mode === "formBar" && "hidden")}>
              <Composer
                ref={composerRef}
                isReply={!transcriptIsEmpty}
                // Caret in the box on arrival, so a confident user can land and
                // start typing without a click. Only on the empty landing state:
                // autofocusing a composer below an existing transcript would
                // scroll the reader to the bottom of a thread they have not read.
                autoFocus={transcriptIsEmpty}
                value={draft}
                onValueChange={setDraft}
                onSubmit={(content, attachments, files) => {
                  unstartedRetryPrompt.current = null;
                  return send(content, attachments, files);
                }}
                isStreaming={stream.isStreaming || isStarting}
                onStop={live === null ? undefined : handleStop}
                disabled={isStarting}
              />
            </div>
          </Card>

          {/* No starter prompts around the box: the chat is the front door,
              and the rotating examples inside it show what to write. The one
              exception (6 Oct, product owner): with the access front door on,
              three chips for people's access, which open "Request access for
              a person" with its type chosen rather than writing into the box.
              Below it — never above, so nothing competes with the one input —
              the few HOME notices IT has published. Landing state only: a
              notice board halfway down a live conversation is noise. */}

          {transcriptIsEmpty ? <MessageCentre /> : null}
        </div>

        {/* The rail shows what is already in flight, not what was asked before.
            Conversation history is still a click away in the drawer — it is
            genuinely useful when a thread is picked up days later — but it is
            not what an employee arrives needing, so it no longer occupies the
            one piece of persistent screen furniture on the page. */}
        {!showRail ? null : (
          <aside className="hidden lg:block" aria-label={tt("activity.title")}>
            <div className="sticky top-6">
              <MySupportRail />
            </div>
          </aside>
        )}
      </div>
    </PageContainer>
  );
}

/**
 * The slim bar in the composer's place while a chat form is open (14 Oct):
 * what to do, a way to type instead, and "New question" to back out.
 */
function FormOpenBar({
  onAskInstead,
  onNewQuestion,
}: {
  onAskInstead: () => void;
  onNewQuestion: () => void;
}) {
  const { t } = useT("assistant");
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" role="status">
      <Typography variant="body-sm" className="min-w-0 flex-1 font-medium">
        {t("inlineForm.barText")}
      </Typography>
      <Button variant="ghost" tone="neutral" size="sm" onClick={onAskInstead}>
        {t("inlineForm.askInstead")}
      </Button>
      <Button
        variant="outline"
        tone="neutral"
        size="sm"
        startIcon={<Icon icon={Plus} size="sm" />}
        onClick={onNewQuestion}
      >
        {t("conversation.new")}
      </Button>
    </div>
  );
}
